import React, { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Menu from "../components/menu";
import DashboardShell from "../../components/DashboardShell";
import { useMenuState } from "../../Common/MenuStateContext";
import SaListPageShell from "../../components/sa/SaListPageShell";
import SettingsTabNav, { type SettingsTabId } from "../../components/sa/settings/SettingsTabNav";
import UserTypesPanel from "../../components/sa/settings/UserTypesPanel";
import SourceTypesPanel from "../../components/sa/settings/SourceTypesPanel";
import DepartmentsPanel from "../../components/sa/settings/DepartmentsPanel";
import SecurityLogsPanel from "../../components/sa/settings/SecurityLogsPanel";
import "../../assets/css/dashboard.css";

const TAB_USER_TYPES: SettingsTabId = "user-types";
const TAB_SOURCE_TYPES: SettingsTabId = "source-types";
const TAB_DEPARTMENTS: SettingsTabId = "departments";
const TAB_SECURITY_LOGS: SettingsTabId = "security-logs";

const SETTINGS_TABS = [
  {
    id: TAB_USER_TYPES,
    label: "User types",
    description: "Used on projects, capabilities, and related forms.",
  },
  {
    id: TAB_SOURCE_TYPES,
    label: "Source types",
    description: "Used on source document upload and framework forms.",
  },
  {
    id: TAB_DEPARTMENTS,
    label: "Departments",
    description: "Used on project create/edit and project setup requests.",
  },
  {
    id: TAB_SECURITY_LOGS,
    label: "Security logs",
    description: "Failed logins, password reset attempts, and OTP failures across the platform.",
  },
];

const PANELS: Record<SettingsTabId, React.ComponentType> = {
  [TAB_USER_TYPES]: UserTypesPanel,
  [TAB_SOURCE_TYPES]: SourceTypesPanel,
  [TAB_DEPARTMENTS]: DepartmentsPanel,
  [TAB_SECURITY_LOGS]: SecurityLogsPanel,
};

const Settings = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab: SettingsTabId =
    tabParam && tabParam in PANELS ? (tabParam as SettingsTabId) : TAB_USER_TYPES;
  const ActivePanel = PANELS[activeTab];

  const setActiveTab = useCallback(
    (id) => {
      setSearchParams(id === TAB_USER_TYPES ? {} : { tab: id }, { replace: true });
    },
    [setSearchParams]
  );

  return (
    <DashboardShell
      isMenuCollapsed={isMenuCollapsed}
      sidebar={
        <Menu
          onSelect={setSelectedItem}
          selectedItem={selectedItem}
          isCollapsed={isMenuCollapsed}
          onToggleCollapse={toggleMenuCollapse}
        />
      }
    >
      <SaListPageShell topBarTitle="Settings">
        <div className="dash-frame-parent1 sa-settings-page">
          <div className="dash-list-toolbar sa-settings-page__header">
            <div className="dash-list-toolbar__intro">
              <h5 className="dash-list-toolbar__title">Platform settings</h5>
              <p className="dash-list-toolbar__desc">
                Manage dropdown options used across forms and review platform security activity.
              </p>
            </div>
          </div>

          <SettingsTabNav tabs={SETTINGS_TABS} activeId={activeTab} onChange={setActiveTab} />

          <div
            className="sa-settings-page__panel"
            role="tabpanel"
            id={`sa-settings-panel-${activeTab}`}
            aria-labelledby={`sa-settings-tab-${activeTab}`}
          >
            <ActivePanel />
          </div>
        </div>
      </SaListPageShell>
    </DashboardShell>
  );
};

export default Settings;
